import get from 'lodash-es/get';
import { en } from './en.js';
import { ja } from './ja.js';
import { ko } from './ko.js';
import { es } from './es.js';
import jaGenerated from './generated/ja.json';
import koGenerated from './generated/ko.json';
import esGenerated from './generated/es.json';
import { completeLocaleMessages } from './locale-merge';
import { reactive, type App } from 'vue';

/**
 * 支持的语言列表（按 native label 显示，避免英文歧视感）。
 * 顺序也会决定 Footer 切换器的展示顺序。
 *
 * 注意：当前不提供 zh-CN（简体中文）翻译。代码中 `isChinese` getter 与
 * `prompt-examples.js` / `ai-design-tools/[slug].vue` 等业务模块仍会基于
 * locale 启发判断 zh，这是历史兼容路径；landing 站本身的 marketing 文案
 * 不再透过 LanguageSwitcher 暴露 zh 选项。
 */
export type LocaleCode = 'en' | 'ja' | 'ko' | 'es';

export const languages: Array<{ code: LocaleCode; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'es', label: 'Español' },
];

export const LANGUAGE_SETTING_KEY = 'veeso-lang';

const LOCALE_COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 365;

export const messages: Record<string, Record<string, unknown>> = {
  en: en,
  ja: completeLocaleMessages(en, ja, jaGenerated),
  ko: completeLocaleMessages(en, ko, koGenerated),
  es: completeLocaleMessages(en, es, esGenerated),
};

interface I18NOptions {
  locale: string;
  fallbackLocale: string;
  messages: Record<string, Record<string, unknown>>;
}

/** 把任意输入规范成受支持的 locale code，非法则返回 null */
export function normalizeLocale(raw: string | null | undefined): LocaleCode | null {
  if (!raw) return null;
  const code = raw.toLowerCase().trim().split('-')[0];
  if (code === 'en' || code === 'ja' || code === 'ko' || code === 'es') return code;
  return null;
}

/**
 * 统一 locale 决策（SSR / CSR 共用同一套优先级）：
 *   1. URL 路径前缀（/ja、/ko、/es）
 *   2. URL ?lang=
 *   3. Cookie（SSR 可读，刷新时不闪语言）
 *   4. localStorage（仅客户端；用于迁移旧偏好并回写 cookie）
 *   5. 默认 en
 */
export function resolveLocale(sources: {
  pathLang?: string | null;
  urlLang?: string | null;
  cookieLang?: string | null;
  storageLang?: string | null;
}): LocaleCode {
  return (
    normalizeLocale(sources.pathLang) ??
    normalizeLocale(sources.urlLang) ??
    normalizeLocale(sources.cookieLang) ??
    normalizeLocale(sources.storageLang) ??
    'en'
  );
}

/** 客户端把 locale 写入 document.cookie，供下次 SSR 请求携带 */
export function writeLocaleCookie(locale: LocaleCode): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${LANGUAGE_SETTING_KEY}=${encodeURIComponent(locale)}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE_SEC}; SameSite=Lax`;
}

/** 持久化用户语言偏好（localStorage + cookie） */
export function persistLocalePreference(locale: LocaleCode): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(LANGUAGE_SETTING_KEY, locale);
  }
  writeLocaleCookie(locale);
}

class I18N {
  options: I18NOptions;
  global: { t: (key: string, params?: Record<string, string>) => string };

  constructor(options: I18NOptions) {
    this.options = reactive(options) as I18NOptions;
    this.global = {
      t: (key: string, params?: Record<string, string>) => this.get(key, params),
    };
  }

  get locale(): string {
    return this.options.locale;
  }

  get isChinese(): boolean {
    return this.locale.toLowerCase().startsWith('zh');
  }

  get language(): string {
    return this.locale.split('-')[0] || '';
  }

  t(key: string, params?: Record<string, string>): string {
    return this.get(key, params);
  }

  get(
    key: string,
    params?: Record<string, string | number> | string,
    language?: Record<string, Record<string, unknown>>
  ): string {
    const raw = this.getRaw(key, language);
    let result = typeof raw === 'string' ? raw : undefined;
    if (params instanceof Object && typeof params !== 'string' && typeof result === 'string') {
      for (const [paramKey, paramValue] of Object.entries(params)) {
        result = result.replace(new RegExp(`{{${paramKey}}}`, 'g'), String(paramValue));
      }
    }
    if (!result && typeof params === 'string') return params;
    return result || '';
  }

  /** 读取任意类型的 message（如 string[]），供 FAQ / flying_text 等使用 */
  getRaw(key: string, language?: Record<string, Record<string, unknown>>): unknown {
    const { locale, fallbackLocale, messages } = this.options;
    const lang = language || messages;
    return get(lang[locale], key, get(lang[fallbackLocale], key));
  }

  /** 切换语言并持久化（localStorage + cookie，供 SSR 下次请求读取） */
  setLocale(locale: string): void {
    const normalized = normalizeLocale(locale) ?? 'en';
    this.options.locale = normalized;
    persistLocalePreference(normalized);
  }
}

/**
 * 模块加载时的占位 locale。
 * 真实 locale 在 Nuxt plugin（enforce: 'pre'）里通过 cookie / URL / localStorage 解析，
 * 必须在首屏 SSR 渲染前写入，避免「先英文后日文」的 hydration 闪烁。
 */
export const i18n = new I18N({
  locale: 'en',
  fallbackLocale: 'en',
  messages: messages,
});

export function tArray(key: string): string[] {
  const raw = i18n.getRaw(key);
  return Array.isArray(raw) ? (raw as string[]) : [];
}

export function t(
  key: string,
  params?: Record<string, string | number>,
  lang?: Record<string, Record<string, unknown>>
): string {
  const result = i18n.get(key, params, lang);
  if (result) return result;
  if (typeof params === 'string') return params;
  return '';
}

export function $t(
  key: string,
  v?: string,
  params?: Record<string, string>,
  lang?: Record<string, Record<string, unknown>>
): string {
  return i18n.get(key, params, lang) || v || '';
}

export default {
  install: (app: App): void => {
    // inject a globally available $translate() method
    app.config.globalProperties.$t = (
      key: string,
      params?: Record<string, string> | string,
      lang?: Record<string, Record<string, unknown>>
    ): string => {
      if (typeof params === 'object') {
        return i18n.get(key, params, lang);
      } else if (typeof params === 'string') {
        return i18n.get(key, '', lang) || params;
      }
      return i18n.get(key, params, lang);
    };
  },
};
